/** @jsxImportSource @emotion/react */
import { Tag } from "primereact/tag";
import { Boton, formatFecha } from "../../../shared";
import Flujograma from "../../../elementos/Flujograma";

// Flujograma de una actividad: el flujo real de su tipo, con los pasos que
// están abiertos marcados. El dibujo no cambia con las decisiones: si un paso
// se decidió varias veces (porque la actividad avanzó y volvió), todas sus
// decisiones se listan dentro de la misma caja.

const ANCHO_PASO = 250;

// Altos de cada parte de la caja, para calcular el alto total (las cajas se
// ubican con posición fija, así que el alto no puede salir del contenido)
const ALTO_BASE = 66; // márgenes, nombre y tipo de paso
const ALTO_ETIQUETA = 26;
const ALTO_LINEA = 19;
const ALTO_EXTRA = 30;
const ALTO_DECISION = 24;
const ALTO_BOTON = 28;

const unaLinea = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
};

const nombreDe = (persona) =>
  [persona?.nombre, persona?.apellido].filter(Boolean).join(" ");

/** Decisiones de un paso (de colaboradores y administrativas), por fecha. */
export function decisionesDePaso(paso) {
  return [
    ...(paso.actividadpaso_x_auditor || []).flatMap((asignacion) =>
      (asignacion.actividadpasodecision || []).map((decision) => ({
        decision,
        esAdmin: false,
        auditor: asignacion.auditor,
        nombre: nombreDe(asignacion.auditor),
        fecha: new Date(decision.fechadecision)
      }))
    ),
    ...(paso.actividadpasodecisionadmin || []).map((decision) => ({
      decision,
      esAdmin: true,
      administrador: decision.administrador,
      nombre: nombreDe(decision.administrador),
      fecha: new Date(decision.fechadecision)
    }))
  ].sort((a, b) => a.fecha - b.fecha);
}

// Personas asignadas a un paso, en líneas "Rol: nombres"
function asignadosDePaso(paso) {
  const colaboradores = (paso.actividadpaso_x_auditor || []).filter(
    (a) => a.activo !== false
  );
  const administradores = (paso.actividadpaso_x_administrador || []).map(
    (a) => {
      const rol = a.usuario.Usuario_x_Acceso?.[0]?.acceso.nombre;
      return `${nombreDe(a.usuario)}${rol ? ` (${rol})` : ""}`;
    }
  );
  return [
    ["Decisor", colaboradores.filter((a) => a.decisor).map((a) => nombreDe(a.auditor))],
    ["En formación", colaboradores.filter((a) => !a.decisor).map((a) => nombreDe(a.auditor))],
    ["Administrador", administradores]
  ].filter(([, nombres]) => nombres.length > 0);
}

/**
 * @param {object} actividad la actividad, con sus pasos y su `flujo`
 *   ({ pasoInicialId, transiciones }, por id de paso configurado)
 * @param {(paso) => React.ReactNode} [extraDePaso] bloque propio de un paso
 *   (el ensayo, en los pasos de tipo Ensayo); null si el paso no lleva
 * @param {(paso, decisiones) => void} onVerDetalles abre el detalle de un paso
 */
export default function FlujoActividad({
  actividad,
  extraDePaso,
  onVerDetalles
}) {
  const finalizada = !!actividad.fechafinalizacion;

  // El flujo es el propio de la actividad, entre sus pasos
  const pasos = (actividad.actividadpaso || [])
    .slice()
    .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0))
    .map((paso) => {
      const decisiones = decisionesDePaso(paso);
      const asignados = asignadosDePaso(paso);
      const extra = extraDePaso ? extraDePaso(paso) : null;
      // Abierto: es un paso actual de una actividad sin finalizar
      const abierto = paso.actual && !finalizada;
      // Su rama ya terminó y espera a las demás para seguir
      const esperando = paso.ramaCompletada && !finalizada;
      return {
        id: paso.id,
        paso,
        decisiones,
        asignados,
        extra,
        abierto,
        esperando,
        alto:
          ALTO_BASE +
          (abierto || esperando ? ALTO_ETIQUETA : 0) +
          asignados.length * ALTO_LINEA +
          (extra ? ALTO_EXTRA : 0) +
          decisiones.length * ALTO_DECISION +
          ALTO_BOTON
      };
    });

  if (pasos.length === 0) {
    return (
      <p css={{ color: "var(--grisTextos)" }}>No hay pasos en esta actividad</p>
    );
  }

  return (
    <Flujograma
      pasos={pasos}
      transiciones={actividad.flujo?.transiciones || []}
      pasoInicialId={actividad.flujo?.pasoInicialId ?? null}
      anchoPaso={ANCHO_PASO}
      altoDePaso={(nodo) => nodo.alto}
      estiloDePaso={(nodo) => ({
        padding: "8px 10px",
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        // Los pasos abiertos van marcados
        ...(nodo.abierto
          ? {
              border: "2px solid var(--amarillo)",
              backgroundColor: "var(--amarilloLight)"
            }
          : {}),
        "& .p-button": { padding: "0.15em 0.3em" }
      })}
      renderPaso={(nodo) => (
        <>
          <strong css={{ fontSize: "0.9rem", ...unaLinea }}>
            {nodo.paso.nombre}
          </strong>
          <div css={{ color: "var(--grisTextos)", ...unaLinea }}>
            {nodo.paso.pasoTipo?.nombre || ""}
          </div>

          {nodo.abierto && (
            <div>
              <Tag value="Paso actual" severity="warning" />
            </div>
          )}
          {!nodo.abierto && nodo.esperando && (
            <div>
              <Tag value="Esperando a otros pasos" severity="info" />
            </div>
          )}

          {nodo.asignados.map(([rol, nombres]) => (
            <div key={rol} css={{ color: "var(--grisTextos)", ...unaLinea }}>
              <strong>{rol}:</strong> {nombres.join(", ")}
            </div>
          ))}

          {nodo.extra}

          {/* Una línea por cada decisión que se tomó en el paso */}
          {nodo.decisiones.map((item) => (
            <div
              key={`${item.esAdmin ? "admin" : "colab"}-${item.decision.id}`}
              css={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "6px"
              }}
            >
              <span css={unaLinea}>
                {formatFecha(item.decision.fechadecision)} · {item.nombre}
              </span>
              <Tag
                value={item.decision.aprobado ? "Aprobado" : "Rechazado"}
                severity={item.decision.aprobado ? "success" : "danger"}
                css={{ fontSize: "0.65rem", padding: "1px 6px", flexShrink: 0 }}
              />
            </div>
          ))}

          <div css={{ marginTop: "auto" }}>
            <Boton
              texto
              chico
              callback={() => onVerDetalles(nodo.paso, nodo.decisiones)}
            >
              Ver detalles
            </Boton>
          </div>
        </>
      )}
    />
  );
}
